import { DOCUMENT_MAX_SIZE, DOCUMENT_MIME_TYPES } from '@lucidtech/las-sdk-browser';
import { memo, useCallback, useState } from 'react';

import SaveIcon from '@/assets/save-icon.svg?react';
import { DropZone, DropZoneButton, DropZoneProps, ErrorCard, Text } from '@/components/core';
import { EMAIL_CONTENT_TYPE } from '@/constants';
import { useAgent, useTour } from '@/hooks';
import { sleep } from '@/utils';

type UploadProgressProps = {
  uploadProgress: number;
};

const IUploadProgress = ({ uploadProgress }: UploadProgressProps) => {
  return (
    <>
      <Text className="flex gap-2">
        <SaveIcon className="size-4 animate-spin stroke-gray-500" />
        Uploading files...
      </Text>
      <Text size="xs">({uploadProgress} completed)</Text>
    </>
  );
};

const UploadProgress = memo(IUploadProgress) as typeof IUploadProgress;

export type UploadDocumentsDropzoneProps = Omit<DropZoneProps, 'children'> & {
  agentId?: string;
  onUploadSuccess?: () => void;
};

const IUploadDocumentsDropzone = ({ className, agentId, onUploadSuccess, ...rest }: UploadDocumentsDropzoneProps) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<Error | undefined | null>();
  const [isUploading, setIsUploading] = useState(false);
  const { createRun } = useAgent({ agentId });

  const { setShowSampleDocumentTooltip } = useTour({ prefixes: [agentId] });

  const onUploadFiles = useCallback(
    async (files: File[]) => {
      setUploadProgress(0);
      setUploadError(undefined);
      setIsUploading(true);

      try {
        for (const file of files ?? []) {
          await createRun({ file });
          setUploadProgress((x) => x + 1);
          await sleep(2000);
        }

        onUploadSuccess?.();
      } catch (error) {
        if (error instanceof Error) {
          setUploadError(error);
        } else {
          setUploadError(new Error('An unknown error occurred during file upload.'));
        }
      } finally {
        setIsUploading(false);
      }

      setShowSampleDocumentTooltip(false);
    },
    [createRun, onUploadSuccess, setShowSampleDocumentTooltip]
  );

  return (
    <div className="gap-sm flex flex-col">
      <DropZone
        className={className}
        allowsMultiple
        acceptedMimeTypes={[...DOCUMENT_MIME_TYPES].filter((mimeType) => mimeType !== EMAIL_CONTENT_TYPE)}
        maxFileSize={DOCUMENT_MAX_SIZE}
        onDropFiles={onUploadFiles}
        isDisabled={isUploading}
        {...rest}
      >
        {isUploading ? <UploadProgress uploadProgress={uploadProgress} /> : <DropZoneButton />}
      </DropZone>
      {uploadError ? <ErrorCard label="Upload failed" error={uploadError} /> : null}
    </div>
  );
};

export const UploadDocumentsDropzone = memo(IUploadDocumentsDropzone) as typeof IUploadDocumentsDropzone;
